import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_suptrans` raw rows into
 * `ap_internal_purchase_orders` + line items domain rows.
 *
 * Stage 2 of the SUPTRANS ingest pipeline. Runs after `master_data_bulk_insert`
 * has populated `rexel_suptrans`. For each row in the latest batch:
 *   - Resolves vendor code → `ap_suppliers.id`
 *   - Resolves product SKU → `im_products_v2.id`
 *   - Resolves warehouse code → `im_warehouses.id`
 *   - Upserts into `ap_internal_purchase_orders` and associated line items
 *
 * Scoping: `updatedAfter` (typically wired from upstream `startedAt`) limits
 * the rexel_suptrans rows considered to those touched by the current run.
 */
export const transformSuptransPurchaseOrdersStep = defineStepConfig({
  type: "transform_suptrans_purchase_orders",
  name: "Transform SUPTRANS → Purchase Orders",
  description:
    "Reads rexel_suptrans rows updated by the current run, FK-resolves vendor + product + warehouse, and upserts into ap_internal_purchase_orders + line items.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process rows updated after this time. " +
          "Typically wired from the upstream master_data_bulk_insert's startedAt output.",
      ),
  }),
  outputSchema: z.object({
    totalRows: z
      .number()
      .describe("Total number of rexel_suptrans rows processed"),
    posCreated: z
      .number()
      .describe("Number of new ap_internal_purchase_orders rows created"),
    posUpdated: z
      .number()
      .describe("Number of existing ap_internal_purchase_orders rows updated"),
    lineItemsCreated: z
      .number()
      .describe("Number of purchase order line item rows created"),
    lineItemsUpdated: z
      .number()
      .describe("Number of purchase order line item rows updated"),
    skippedProductCount: z
      .number()
      .describe(
        "Number of distinct product SKUs skipped because they don't exist in im_products_v2",
      ),
    skippedWarehouseCount: z
      .number()
      .describe(
        "Number of distinct warehouse codes skipped (not found in im_warehouses)",
      ),
    skippedSupplierCount: z
      .number()
      .describe(
        "Number of distinct vendor codes skipped (not found in ap_suppliers)",
      ),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the input timestamp so downstream steps can scope to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe("Number of rows to process per batch"),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
